import React from "react"
import { Arc } from "@visx/shape"
import { springDelay } from "./utils/animationUtils"

const TICK_COUNT = 60
const GAP_DEGREES = 90
const ARC_DEGREES = 360 - GAP_DEGREES
const START_ANGLE_DEG = 90 + GAP_DEGREES / 2 // 135° from SVG x-axis
const CENTER = 100
const OUTER_RADIUS = 88
const INNER_RADIUS = 73
const TICK_WIDTH = 1

const TRACK_RADIUS = 64

const ON_COLOR = "#00F9A1"
const ON_FILTER = "drop-shadow(0 0 3px rgba(0, 249, 161, 0.50))"
const OFF_COLOR = "#00F9A133" // ON_COLOR with 20% opacity
const TRACK_COLOR = "rgba(255, 255, 255, 0.12)"

// visx Arc uses d3 convention: 0 = 12 o'clock, clockwise. +π/2 converts from SVG x-axis convention.
const TRACK_START_ANGLE = ((START_ANGLE_DEG + 90) * Math.PI) / 180
const TRACK_END_ANGLE = ((START_ANGLE_DEG + ARC_DEGREES + 90) * Math.PI) / 180

export interface GaugeChartProps {
    percentage: number // 0 to 1
    subtitle: string
}

export const GaugeChart = ({ percentage, subtitle }: GaugeChartProps) => {
    const activeCount = Math.round(percentage * TICK_COUNT)

    return (
        <svg viewBox="0 0 200 200" className="w-full h-full" key={percentage}>
            <g transform={`translate(${CENTER}, ${CENTER})`}>
                <Arc
                    startAngle={TRACK_START_ANGLE}
                    endAngle={TRACK_END_ANGLE}
                    innerRadius={TRACK_RADIUS - 0.5}
                    outerRadius={TRACK_RADIUS + 0.5}
                    fill={TRACK_COLOR}
                />
            </g>
            {Array.from({ length: TICK_COUNT }, (_, i) => {
                const angleDeg = START_ANGLE_DEG + (i / (TICK_COUNT - 1)) * ARC_DEGREES
                const angleRad = (angleDeg * Math.PI) / 180
                const x1 = CENTER + INNER_RADIUS * Math.cos(angleRad)
                const y1 = CENTER + INNER_RADIUS * Math.sin(angleRad)
                const x2 = CENTER + OUTER_RADIUS * Math.cos(angleRad)
                const y2 = CENTER + OUTER_RADIUS * Math.sin(angleRad)
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        strokeWidth={TICK_WIDTH}
                        stroke={OFF_COLOR}
                    />
                )
            })}
            {Array.from({ length: activeCount }, (_, i) => {
                const angleDeg = START_ANGLE_DEG + (i / (TICK_COUNT - 1)) * ARC_DEGREES
                const angleRad = (angleDeg * Math.PI) / 180
                const x1 = CENTER + INNER_RADIUS * Math.cos(angleRad)
                const y1 = CENTER + INNER_RADIUS * Math.sin(angleRad)
                const x2 = CENTER + OUTER_RADIUS * Math.cos(angleRad)
                const y2 = CENTER + OUTER_RADIUS * Math.sin(angleRad)
                const delay = springDelay(i / activeCount, 600)
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        strokeWidth={TICK_WIDTH}
                        stroke={ON_COLOR}
                        style={{ filter: ON_FILTER, animationDelay: `${delay}ms` }}
                        className="motion-safe:animate-pip-fill"
                    />
                )
            })}
            <text
                x={CENTER}
                y={CENTER - 8}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                style={{
                    fontSize: 32,
                    fontWeight: 300,
                    fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
            >
                {Math.round(percentage * 100)}%
            </text>
            <text
                x={CENTER}
                y={CENTER + 16}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="rgba(255,255,255,0.4)"
                style={{
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: "0.01em",
                    fontFamily: "var(--raleway)",
                }}
            >
                {subtitle.toUpperCase()}
            </text>
        </svg>
    )
}
